import React from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useGlobalState } from 'state'
import { LAYERS } from 'common/layers'
import { Protocol } from 'pmtiles'
import { EF_TOKUNICHUUI_DEFAULT_URL } from 'common/consts'
import dayjs from 'dayjs'

export const Map = ({ children }: React.PropsWithChildren) => {
  const state = useGlobalState()

  const map = React.useRef<maplibregl.Map | null>(null)
  const mapContainer = React.useRef<HTMLDivElement | null>(null)

  maplibregl.addProtocol('pmtiles', new Protocol().tile)

  const baseMapStyle: maplibregl.StyleSpecification = {
    version: 8,
    sources: {
      base: {
        type: 'raster',
        tiles: [state.map.style.url],
        tileSize: 256,
        attribution: '地図の出典：<a href=\'https://www.gsi.go.jp/\' target=\'_blank\'>地理院タイル</a>',
      },
    },
    layers: [
      {
        id: 'base',
        type: 'raster',
        source: 'base',
      },
    ],
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf'
  }

  React.useEffect(() => {
    if (map.current) return
    if (!mapContainer.current) return

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: baseMapStyle,
      center: [state.map.lngLat.lng, state.map.lngLat.lat],
      minZoom: 2,
      maxZoom: 19,
      zoom: state.map.zoom,
      // antialias: false
    })

    map.current.addControl(new maplibregl.ScaleControl(), 'bottom-left')
    map.current.addControl(new maplibregl.NavigationControl({showZoom: false, showCompass: true, visualizePitch: true}), 'bottom-left')

    map.current.on('move', () => {
      if (!map.current) return
      state.map.setLatLng(map.current.getCenter())
    })

    map.current.on('moveend', () => {
      if (!map.current) return
      state.map.setBounds(map.current.getBounds())
    })

    map.current.on('zoomend', () => {
      if (!map.current) return
      state.map.setZoom(map.current.getZoom())
      state.map.setIntZoom(Math.floor(map.current.getZoom()))
    })

    map.current.on('load', () => {
      if (!map.current) return
      Object.values(LAYERS).forEach(layer => {
        map.current?.addLayer(layer)
      })
      state.map.setMapbox(map.current)
    })

    map.current.on('error', (e) => {
      if (!map.current) return
      if (!e || !e.error) return
      if (e.error.name === 'AbortError') {
        return
      }
      if (e.error.statusText === 'signal is aborted without reason') {
        return
      }
      if (e.error.status === 404 || e.error.status === 403) {
        return
      }
      if (e.error.message && e.error.message === 'Invalid PMTiles protocol URL') {
        return
      }
      console.error('Maplibre Error:', e)
    })

    state.app.loadImages()

    return () => {
      state.map.setMapbox(null)
    }
  }, [])

  React.useEffect(() => {
    if (!state.mapbox) return
    const source = state.mapbox.getSource('base') as maplibregl.RasterTileSource
    if (source) {
      source.setTiles([state.map.style.url])
    }
  }, [state.map.style])

  React.useEffect(() => {
    if (!map.current) return
    if (state.app.menu === 'river-floods') {
      if (map.current.getZoom() > 15) {
        map.current.setZoom(15)
      }
      map.current.setMaxZoom(15)
    } else {
      if (map.current.getMaxZoom() !== 19) {
        map.current.setMaxZoom(19)
      }
    }
  }, [state.app.menu])

  React.useEffect(() => {
    visibleLayers()
  }, [state.mapbox])

  const visibleLayers = () => {
    const isRiverFloods = state.app.menu === 'river-floods'
    state.map.setLayoutVisibility(LAYERS.HINAN_AREA_ALL.id, state.app.estimatedFloodingLayers.includes(LAYERS.HINAN_AREA_ALL.id))
    state.map.setLayoutVisibility(LAYERS.RIVER.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.RIVER.id))
    state.map.setLayoutVisibility(LAYERS.SCHOOL_AREA_RISK.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.SCHOOL_AREA_RISK.id))
    state.map.setLayoutVisibility(LAYERS.SCHOOL_AREA_RISK_PURPLE.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.SCHOOL_AREA_RISK.id))
    state.map.setLayoutVisibility(LAYERS.SCHOOL_AREA_RISK_BLACK.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.SCHOOL_AREA_RISK.id))
    state.map.setLayoutVisibility(LAYERS.EF_SHINSUISOUTEI.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.EF_SHINSUISOUTEI.id))
    state.map.setLayoutVisibility(LAYERS.EF_TOKUNICHUUI.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.EF_TOKUNICHUUI.id))
    state.map.setLayoutVisibility(LAYERS.EF_SHINSUISOUTEI_COLOR.id, isRiverFloods && state.app.estimatedFloodingLayers.includes(LAYERS.EF_SHINSUISOUTEI_COLOR.id))
  }

  React.useEffect(() => {
    if (state.app.estimatedFloodingLayers.includes(LAYERS.EF_TOKUNICHUUI.id)) {
      loadTokunichuuiSourceData(state.app.baseDate)
    }
  }, [state.app.estimatedFloodingLayers, state.app.baseDate, state.mapbox])

  const loadTokunichuuiSourceData = React.useCallback(async (date: dayjs.Dayjs) => {
    if (!state.mapbox) return
    // 3回リトライ
    for (let i = 0; i < 3; i++) {
      try {
        const key = 'floodrisk_1min_horaku/' + date.subtract(i, 'minutes').format('YYYYMMDD/HHmm.png')
        const url = `https://d1zetlo8apprf7.cloudfront.net/${key}`
        console.log('Loading Tokunichuui source data from URL:', url)
        const response = await fetch(url, { method: 'HEAD' })
        if (response.ok) {
          state.map.updateImageSource(LAYERS.EF_TOKUNICHUUI.id, url)
          return
        }
      } catch (e) {
        console.error('Error loading Tokunichuui source data:', e)
      }
    }
    state.map.updateImageSource(LAYERS.EF_TOKUNICHUUI.id, EF_TOKUNICHUUI_DEFAULT_URL)
  }, [state.mapbox])

  React.useEffect(() => {
    visibleLayers()
  }, [state.app.estimatedFloodingLayers, state.app.menu])

  return (
    <>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }}></div>
      {children}
    </>
  )
}
